import { createI18n } from "vue-i18n";
import { messages } from "./locales";
/**
 * Canonical full BCP-47 language tags the app uses as locale values (mirrors eudonet-opay-front /
 * eudonet-oportal). Translation bundles stay keyed by the 2-letter Tolgee stems (en / fr / nl, plus an
 * optional fr-CA overrides bundle — see ./locales); the full tags resolve onto those bundles via the
 * `fallbackLocale` chain below, so fr-FR and fr-CA both render the single French bundle with NO
 * duplicated translations. fr-CA is kept distinct as a first-class switcher option.
 */
export const SUPPORTED_LOCALES = ["en-US", "fr-FR", "fr-CA", "nl-NL"];
/** Ultimate fallback: French (the OnePlatform default for this Canadian-facing public app). */
const FALLBACK_LOCALE = "fr-FR";
/**
 * Normalise a raw locale tag to a supported full tag.
 * An exact supported match wins (case-insensitive on the region, so "fr-ca" -> "fr-CA"); otherwise
 * the tag collapses to its primary subtag's canonical full tag ("en-GB" -> "en-US", "fr"/"fr-FR"/
 * "fr-BE" -> "fr-FR", "nl-BE" -> "nl-NL"). fr-CA is the only region-specific French kept distinct, so
 * every other French variant resolves to fr-FR. Returns null for an unsupported language.
 */
function normalise(tag) {
    const trimmed = tag.trim();
    if (!trimmed) {
        return null;
    }
    const lower = trimmed.toLowerCase();
    const exact = SUPPORTED_LOCALES.find((locale) => locale.toLowerCase() === lower);
    if (exact) {
        return exact;
    }
    const primary = lower.split("-")[0];
    if (primary === "fr") {
        return "fr-FR";
    }
    if (primary === "en") {
        return "en-US";
    }
    if (primary === "nl") {
        return "nl-NL";
    }
    return null;
}
/**
 * Resolve the initial UI locale.
 * Order: ?lang= query param -> Accept-Language header -> fr-FR.
 *
 * @param search       window.location.search (e.g. "?lang=en"); pass "" if none.
 * @param acceptLanguage the Accept-Language header value; pass "" if unavailable.
 */
export function resolveInitialLocale(search, acceptLanguage) {
    const lang = new URLSearchParams(search).get("lang");
    if (lang) {
        const fromQuery = normalise(lang);
        if (fromQuery) {
            return fromQuery;
        }
    }
    for (const part of acceptLanguage.split(",")) {
        const tag = part.split(";")[0];
        const fromHeader = normalise(tag);
        if (fromHeader) {
            return fromHeader;
        }
    }
    return FALLBACK_LOCALE;
}
/** localStorage key holding the user's explicit language choice (mirrors eudonet-oportal). */
const STORAGE_KEY = "locale";
function readStoredLocale() {
    if (typeof localStorage === "undefined") {
        return null;
    }
    const stored = localStorage.getItem(STORAGE_KEY);
    // normalise() also migrates a legacy bare 2-letter value (e.g. "fr") to its full tag ("fr-FR").
    return stored ? normalise(stored) : null;
}
/**
 * Resolve the boot locale. A previously-saved choice (localStorage) wins so the visitor's manual
 * language selection survives reloads; otherwise fall back to ?lang= / Accept-Language / fr-FR.
 */
const initialLocale = readStoredLocale() ??
    resolveInitialLocale(typeof window !== "undefined" ? window.location.search : "", typeof navigator !== "undefined" ? navigator.language : "");
const i18n = createI18n({
    locale: initialLocale,
    // Full-tag locales resolve onto the 2-letter Tolgee bundles, and everything ultimately falls back to
    // the French bundle. fr-CA carries only Canada-specific overrides (if any) and falls back to fr.
    fallbackLocale: {
        "fr-CA": ["fr"],
        "fr-FR": ["fr"],
        "en-US": ["en", "fr"],
        "nl-NL": ["nl", "fr"],
        default: ["fr"],
    },
    messages,
    legacy: false,
    globalInjection: true,
    warnHtmlMessage: false,
    // Warn only when a key is missing from every bundle; stay silent when it resolves via fallback.
    missingWarn: true,
    fallbackWarn: false,
});
if (typeof document !== "undefined") {
    document.documentElement.lang = initialLocale;
}
// vue-i18n narrows `i18n.global.locale` to the message-bundle stems (en / fr / fr-CA / nl),
// but the app drives the locale with the full BCP-47 tags that resolve onto those stems via the
// `fallbackLocale` chain above (same messages + fallback shape as eudonet-opay-front, which sets
// the locale through useI18n()'s string-typed handle). Re-expose the locale ref under the app's
// full-tag `Locale` type so `setLocale` assigns canonical tags.
const activeLocale = i18n.global.locale;
/**
 * Bridge a 2-letter document locale (the donation-form `supportedLocales`, e.g.
 * `fr` / `nl` / `en`, plus the region-specific `fr-CA`) to the host's full
 * BCP-47 tag, so the renderer's LanguageToggle can drive BOTH the engine author
 * content AND the host chrome locale (T-PUB-002 §4 bridge). `fr-CA` is kept
 * distinct; every other tag collapses to its canonical full tag via `normalise`.
 * Falls back to the ultimate French tag for an unknown stem (never throws).
 */
export function docLocaleToHostTag(docLocale) {
    return normalise(docLocale) ?? FALLBACK_LOCALE;
}
/**
 * Bridge the host's full BCP-47 tag to a 2-letter document locale (the donation
 * form speaks 2-letter locales; `fr-CA` is preserved). Used to seed the
 * renderer's initial author-content language from the current chrome locale.
 */
export function hostTagToDocLocale(tag) {
    const lower = tag.trim().toLowerCase();
    if (lower === "fr-ca") {
        return "fr-CA";
    }
    return lower.split("-")[0];
}
/** Languages offered in the in-page switcher (full tags; fr-CA is a first-class option). */
export const SWITCHABLE_LOCALES = [
    "fr-FR",
    "en-US",
    "nl-NL",
    "fr-CA",
];
/**
 * Switch the active UI locale and persist the choice (mirrors eudonet-oportal's `setLocale`):
 * normalises to a supported full tag, updates the reactive vue-i18n locale, saves to localStorage,
 * and reflects it on `<html lang>`. No-ops on an unsupported tag.
 */
export function setLocale(tag) {
    const next = normalise(tag);
    if (!next) {
        return;
    }
    activeLocale.value = next;
    if (typeof localStorage !== "undefined") {
        localStorage.setItem(STORAGE_KEY, next);
    }
    if (typeof document !== "undefined") {
        document.documentElement.lang = next;
    }
}
/**
 * sessionStorage key holding the user's EXPLICIT in-session language pick (the in-page
 * LanguageSwitcher). It survives route navigation and an in-tab reload but is cleared when the tab
 * closes — i.e. it lives for the FE session. Kept DISTINCT from the localStorage `locale` boot
 * choice, which is ALSO written by programmatic, server-resolved applications (`setLocale`, e.g. the
 * email-preferences contact-snapshot locale) and so can't be trusted to mean "the user chose this".
 */
const SESSION_SELECTED_KEY = "selected-locale";
/**
 * The user's explicit in-session locale choice, normalised to a supported full tag, or null when
 * none. Consumers replay it (e.g. EmailPreferencesView on mount) so a deliberate switch outranks a
 * server-resolved default (the contact snapshot) for the rest of the tab session.
 */
export function getSessionSelectedLocale() {
    if (typeof sessionStorage === "undefined") {
        return null;
    }
    const stored = sessionStorage.getItem(SESSION_SELECTED_KEY);
    // normalise() also migrates a legacy bare 2-letter value (e.g. "fr") to its full tag ("fr-FR").
    return stored ? normalise(stored) : null;
}
/**
 * Apply a locale the USER explicitly picked (the in-page LanguageSwitcher) — as opposed to a
 * programmatic application of a server-resolved value, which goes through `setLocale`. Records the
 * choice in sessionStorage so it survives navigation/reload for the tab session, then applies it via
 * `setLocale`. No-ops on an unsupported tag (mirrors `setLocale`).
 */
export function selectLocale(tag) {
    const next = normalise(tag);
    if (!next) {
        return;
    }
    if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem(SESSION_SELECTED_KEY, next);
    }
    setLocale(next);
}
export default i18n;
